import React, { useState, useEffect, useRef } from 'react';
import { SavedWhiteboard, whiteboardService } from '../services/whiteboardService';
import { useActiveProject } from '../hooks/useActiveProject';
import '../styles/WhiteboardList.css';
import { WhiteboardListSkeleton } from './SkeletonLoader';

interface WhiteboardListProps {
  onSelectWhiteboard: (whiteboard: SavedWhiteboard) => void;
  onNewBoard: () => void;
}

// Extract the actual Excalidraw elements array from saved data
const getExcalidrawElements = (savedElements: any): any[] => {
  if (!savedElements) return [];
  // Saved as { elements: [...], appState: {...} }
  if (savedElements.elements && Array.isArray(savedElements.elements)) {
    return savedElements.elements.filter((el: any) => !el.isDeleted);
  }
  // Fallback: if it's already an array
  if (Array.isArray(savedElements)) {
    return savedElements.filter((el: any) => !el.isDeleted);
  }
  return [];
};

// Preview Canvas for Excalidraw elements
const WhiteboardPreview: React.FC<{ elements: any[] }> = ({ elements }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (elements.length === 0) return;

    // Bounding box
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    elements.forEach((el: any) => {
      const x = el.x ?? 0;
      const y = el.y ?? 0;
      const w = el.width ?? 0;
      const h = el.height ?? 0;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + w);
      maxY = Math.max(maxY, y + h);
      // For freedraw/line/arrow, also check points
      if (el.points && Array.isArray(el.points)) {
        el.points.forEach((pt: number[]) => {
          minX = Math.min(minX, x + (pt[0] || 0));
          minY = Math.min(minY, y + (pt[1] || 0));
          maxX = Math.max(maxX, x + (pt[0] || 0));
          maxY = Math.max(maxY, y + (pt[1] || 0));
        });
      }
    });
    if (minX === Infinity) return;

    const cw = maxX - minX || 1;
    const ch = maxY - minY || 1;
    const pad = 20;
    const scale = Math.min((canvas.width - pad * 2) / cw, (canvas.height - pad * 2) / ch, 2);
    const offX = (canvas.width - cw * scale) / 2 - minX * scale;
    const offY = (canvas.height - ch * scale) / 2 - minY * scale;

    ctx.save();
    ctx.translate(offX, offY);
    ctx.scale(scale, scale);

    elements.forEach((el: any) => {
      ctx.save();
      const x = el.x ?? 0;
      const y = el.y ?? 0;
      const w = el.width ?? 0;
      const h = el.height ?? 0;
      const stroke = el.strokeColor || '#000';
      const bg = el.backgroundColor || 'transparent';
      const sw = el.strokeWidth || 1;
      const opacity = el.opacity != null ? el.opacity / 100 : 1;
      ctx.globalAlpha = opacity;

      // Apply rotation
      if (el.angle) {
        ctx.translate(x + w / 2, y + h / 2);
        ctx.rotate(el.angle);
        ctx.translate(-(x + w / 2), -(y + h / 2));
      }

      const fillShape = () => {
        if (bg && bg !== 'transparent') {
          ctx.fillStyle = bg;
          ctx.fill();
        }
        ctx.strokeStyle = stroke;
        ctx.lineWidth = sw;
        ctx.stroke();
      };

      switch (el.type) {
        case 'rectangle':
          ctx.beginPath();
          if (el.roundness) {
            const r = Math.min(w, h) * 0.1;
            ctx.moveTo(x + r, y);
            ctx.lineTo(x + w - r, y);
            ctx.quadraticCurveTo(x + w, y, x + w, y + r);
            ctx.lineTo(x + w, y + h - r);
            ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
            ctx.lineTo(x + r, y + h);
            ctx.quadraticCurveTo(x, y + h, x, y + h - r);
            ctx.lineTo(x, y + r);
            ctx.quadraticCurveTo(x, y, x + r, y);
          } else {
            ctx.rect(x, y, w, h);
          }
          ctx.closePath();
          fillShape();
          break;

        case 'ellipse':
          ctx.beginPath();
          ctx.ellipse(x + w / 2, y + h / 2, Math.abs(w / 2), Math.abs(h / 2), 0, 0, Math.PI * 2);
          fillShape();
          break;

        case 'diamond':
          ctx.beginPath();
          ctx.moveTo(x + w / 2, y);
          ctx.lineTo(x + w, y + h / 2);
          ctx.lineTo(x + w / 2, y + h);
          ctx.lineTo(x, y + h / 2);
          ctx.closePath();
          fillShape();
          break;

        case 'line':
        case 'arrow':
          if (el.points && el.points.length >= 2) {
            ctx.strokeStyle = stroke;
            ctx.lineWidth = sw;
            ctx.beginPath();
            el.points.forEach((pt: number[], i: number) => {
              const px = x + (pt[0] || 0);
              const py = y + (pt[1] || 0);
              if (i === 0) ctx.moveTo(px, py);
              else ctx.lineTo(px, py);
            });
            ctx.stroke();
            // Arrowhead
            if (el.type === 'arrow' && el.points.length >= 2) {
              const last = el.points[el.points.length - 1];
              const prev = el.points[el.points.length - 2];
              const px1 = x + (last[0] || 0);
              const py1 = y + (last[1] || 0);
              const px0 = x + (prev[0] || 0);
              const py0 = y + (prev[1] || 0);
              const angle = Math.atan2(py1 - py0, px1 - px0);
              const headLen = Math.max(8, sw * 3);
              ctx.fillStyle = stroke;
              ctx.beginPath();
              ctx.moveTo(px1, py1);
              ctx.lineTo(px1 - headLen * Math.cos(angle - Math.PI / 6), py1 - headLen * Math.sin(angle - Math.PI / 6));
              ctx.lineTo(px1 - headLen * Math.cos(angle + Math.PI / 6), py1 - headLen * Math.sin(angle + Math.PI / 6));
              ctx.closePath();
              ctx.fill();
            }
          }
          break;

        case 'freedraw':
          if (el.points && el.points.length > 1) {
            ctx.strokeStyle = stroke;
            ctx.lineWidth = sw;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.beginPath();
            el.points.forEach((pt: number[], i: number) => {
              const px = x + (pt[0] || 0);
              const py = y + (pt[1] || 0);
              if (i === 0) ctx.moveTo(px, py);
              else ctx.lineTo(px, py);
            });
            ctx.stroke();
          }
          break;

        case 'text':
          if (el.text) {
            ctx.fillStyle = stroke;
            const fontSize = el.fontSize || 16;
            ctx.font = `${fontSize}px ${el.fontFamily === 1 ? 'Virgil, cursive' : el.fontFamily === 2 ? 'Cascadia, monospace' : 'Arial'}`;
            ctx.textBaseline = 'top';
            const lines = el.text.split('\n');
            let ty = y;
            lines.forEach((line: string) => {
              ctx.fillText(line, x, ty);
              ty += fontSize * 1.3;
            });
          }
          break;

        case 'frame':
          ctx.strokeStyle = '#aaa';
          ctx.lineWidth = 1;
          ctx.setLineDash([5, 5]);
          ctx.strokeRect(x, y, w, h);
          ctx.setLineDash([]);
          if (el.name) {
            ctx.fillStyle = '#666';
            ctx.font = '12px Arial';
            ctx.fillText(el.name, x + 4, y - 4);
          }
          break;

        default:
          // For any unknown types, draw a simple rect outline
          if (w > 0 && h > 0) {
            ctx.strokeStyle = stroke;
            ctx.lineWidth = sw;
            ctx.strokeRect(x, y, w, h);
          }
          break;
      }
      ctx.restore();
    });

    ctx.restore();
  }, [elements]);

  return (
    <canvas
      ref={canvasRef}
      width={560}
      height={360}
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  );
};

const WhiteboardList: React.FC<WhiteboardListProps> = ({ onSelectWhiteboard, onNewBoard }) => {
  const [whiteboards, setWhiteboards] = useState<SavedWhiteboard[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState<{ id: string; name: string } | null>(null);
  const [newName, setNewName] = useState('');
  const { activeProjectId } = useActiveProject();

  useEffect(() => {
    loadWhiteboards();
  }, [searchQuery, activeProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadWhiteboards = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await whiteboardService.getWhiteboards(searchQuery, activeProjectId);
      setWhiteboards(data);
    } catch (err) {
      setError('Failed to load whiteboards');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id: string, name: string) => {
    setSelectedBoard({ id, name });
    setShowDeletePopup(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBoard) return;
    try {
      await whiteboardService.deleteWhiteboard(selectedBoard.id);
      loadWhiteboards();
      setShowDeletePopup(false);
      setSelectedBoard(null);
    } catch (err) {
      alert('Failed to delete whiteboard');
      console.error(err);
    }
  };

  const handleDuplicateClick = (id: string, name: string) => {
    setSelectedBoard({ id, name });
    setNewName(`${name} (Copy)`);
    setShowDuplicatePopup(true);
  };

  const handleDuplicateConfirm = async () => {
    if (!selectedBoard || !newName.trim()) return;
    try {
      await whiteboardService.duplicateWhiteboard(selectedBoard.id, newName);
      loadWhiteboards();
      setShowDuplicatePopup(false);
      setSelectedBoard(null);
      setNewName('');
    } catch (err) {
      alert('Failed to duplicate whiteboard');
      console.error(err);
    }
  };

  const handleCancelPopup = () => {
    setShowDuplicatePopup(false);
    setShowDeletePopup(false);
    setSelectedBoard(null);
    setNewName('');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="whiteboard-list-container">
      {/* Header - Single Row */}
      <div className="whiteboard-header-row-1">
        <div className="whiteboard-list-header">
          <h1 className="whiteboard-title">Whiteboards</h1>
          <p className="whiteboard-subtitle">Visualize and collaborate on ideas</p>
        </div>
        <div className="header-right-section">
          <div className="search-bar">
            <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              placeholder="Search whiteboards..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <button className="new-board-button btn-primary-cta" onClick={() => {
            console.log('New Board button clicked in WhiteboardList!');
            onNewBoard();
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            New Whiteboard
          </button>
        </div>
      </div>

      {loading && (
        <div style={{ padding: '8px 0' }}>
          <WhiteboardListSkeleton count={6} />
        </div>
      )}

      {error && (
        <div className="error-state">
          <p>{error}</p>
          <button onClick={loadWhiteboards}>Retry</button>
        </div>
      )}

      {!loading && !error && whiteboards.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">🎨</div>
          <h2>{searchQuery ? 'No whiteboards found' : 'No whiteboards yet'}</h2>
          <p>
            {searchQuery
              ? 'Try a different search term'
              : 'Create your first whiteboard to visualize ideas'}
          </p>
          {!searchQuery && (
            <button className="new-board-button btn-primary-cta" onClick={onNewBoard}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Create Whiteboard
            </button>
          )}
        </div>
      )}

      {!loading && !error && whiteboards.length > 0 && (
        <div className="whiteboard-grid">
          {whiteboards.map((board) => (
            <div key={board.id} className="whiteboard-card">
              <div
                className="whiteboard-preview"
                onClick={() => onSelectWhiteboard(board)}
              >
                {(() => {
                  const els = getExcalidrawElements(board.elements);
                  return els.length > 0 ? (
                    <WhiteboardPreview elements={els} />
                  ) : (
                    <div className="preview-placeholder">
                      <svg className="empty-grid-pattern" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                          <pattern id={`grid-${board.id}`} width="24" height="24" patternUnits="userSpaceOnUse">
                            <circle cx="1" cy="1" r="1" fill="rgba(0,0,0,0.08)" />
                          </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill={`url(#grid-${board.id})`} />
                      </svg>
                      <span className="element-count">Empty board</span>
                    </div>
                  );
                })()}
              </div>

              <div className="whiteboard-info">
                <div className="whiteboard-header-row">
                  <h3 className="whiteboard-name" onClick={() => onSelectWhiteboard(board)}>
                    {board.name}
                  </h3>
                  <div className="whiteboard-icon-actions">
                    <button
                      className="icon-btn duplicate-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicateClick(board.id, board.name);
                      }}
                      title="Duplicate"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    </button>
                    <button
                      className="icon-btn delete-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(board.id, board.name);
                      }}
                      title="Delete"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </button>
                  </div>
                </div>
                <p className="whiteboard-date">{formatDate(board.updated_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Duplicate Popup */}
      {showDuplicatePopup && (
        <div className="modal-overlay" onClick={handleCancelPopup}>
          <div className="modal-popup" onClick={(e) => e.stopPropagation()}>
            <h2>Duplicate Whiteboard</h2>
            <p>Enter a name for the duplicated whiteboard:</p>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Whiteboard name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleDuplicateConfirm();
                if (e.key === 'Escape') handleCancelPopup();
              }}
            />
            <div className="modal-actions">
              <button className="modal-btn-cancel" onClick={handleCancelPopup}>
                Cancel
              </button>
              <button
                className="modal-btn-primary"
                onClick={handleDuplicateConfirm}
                disabled={!newName.trim()}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Popup */}
      {showDeletePopup && selectedBoard && (
        <div className="modal-overlay" onClick={handleCancelPopup}>
          <div className="modal-popup" onClick={(e) => e.stopPropagation()}>
            <h2>Delete Whiteboard</h2>
            <p>Are you sure you want to delete <strong>"{selectedBoard.name}"</strong>?</p>
            <p className="modal-warning">This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="modal-btn-cancel" onClick={handleCancelPopup}>
                Cancel
              </button>
              <button className="modal-btn-danger" onClick={handleDeleteConfirm}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhiteboardList;
